/**
 * The catalog viewer (contract v10.49, ADR 0044 of the back): pdf.js reads the PDF from the
 * bucket **in pieces**, the ones the page on screen needs, so a 400 MB catalog opens on a
 * phone in the street without coming down whole.
 *
 * Loaded only when a catalog is opened (`import()`): pdf.js is a megabyte the rest of the
 * application does not need. Its worker and its data (the fonts a PDF names without
 * carrying them, the decoders of scanned images, the character maps) are served from the
 * application itself, never from a CDN: the CSP allows nothing else (`docs/architecture.md`,
 * «Build y despliegue»).
 *
 * Everything that touches pdf.js is here, behind `openCatalog`, so the screen can be tested
 * without a canvas and pdf.js can change version without touching it.
 *
 * **The legacy build**, not the modern one: the modern one uses what only the newest browsers
 * have (`Map.prototype.getOrInsertComputed`, iterator helpers), and on a phone a year old it
 * opens nothing and says nothing. The legacy one carries those pieces with it (core-js),
 * in the worker too. The Chromium of the user journeys was the first to fail.
 */
import type * as PdfJsModule from 'pdfjs-dist/legacy/build/pdf.mjs';
import workerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker&url';
import type * as PdfViewerModule from 'pdfjs-dist/legacy/web/pdf_viewer.mjs';

import { UserFacingError } from '@/shared/api/errors';

import { BucketReader, type SignedLink } from './api';

type PdfJs = typeof PdfJsModule;
type ViewerModule = typeof PdfViewerModule;

/** In development Vite serves `node_modules` as is; the build copies these four folders to
 * `/pdfjs/` (`pdfjsAssets` in vite.config.ts). */
const ASSETS = import.meta.env.DEV ? '/node_modules/pdfjs-dist/' : '/pdfjs/';

/** What pdf.js asks for at a time. 64 KB by default: a catalog's first page, with its fonts
 * and its images, took dozens of round trips on a mobile line. */
export const CHUNK_BYTES = 256 * 1024;

const MAX_SCALE = 6;
const MIN_SCALE = 0.25;

export const OPEN_FAILED = 'No se pudo abrir el catálogo en el navegador.';
export const NEW_VERSION =
  'Hay una versión nueva de la aplicación. Recarga la página para abrir el catálogo.';

/**
 * pdf.js, its viewer or its worker could not be fetched because the application was
 * deployed again while this tab was open: the files it names have other names now, and
 * asking again finds nothing. Only reloading helps.
 */
export class NewVersionError extends UserFacingError {
  constructor() {
    super(NEW_VERSION);
    this.name = 'NewVersionError';
  }
}

/** What Chrome, Firefox and Safari say when a module or the pdf.js worker is not there. */
const MISSING_FILE =
  /dynamically imported module|importing a module script failed|error loading dynamically imported|fake worker/i;

function isMissingFile(error: unknown): boolean {
  return error instanceof Error && MISSING_FILE.test(error.message);
}

let loading: Promise<{ pdfjs: PdfJs; viewer: ViewerModule }> | null = null;

/** pdf.js and its viewer, once. The viewer module reads pdf.js from `globalThis.pdfjsLib`
 * when it is evaluated, so the order matters. */
function loadPdfJs(): Promise<{ pdfjs: PdfJs; viewer: ViewerModule }> {
  loading ??= (async () => {
    const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    (globalThis as { pdfjsLib?: unknown }).pdfjsLib = pdfjs;
    const [viewer] = await Promise.all([
      import('pdfjs-dist/legacy/web/pdf_viewer.mjs'),
      import('pdfjs-dist/legacy/web/pdf_viewer.css'),
    ]);
    return { pdfjs, viewer };
  })().catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

export interface OpenedCatalog {
  pages: number;
  page(): number;
  goTo(page: number): void;
  zoomIn(): void;
  zoomOut(): void;
  fitWidth(): void;
  destroy(): void;
}

export interface OpenOptions {
  /** Absolutely positioned and scrolling: pdf.js refuses anything else. */
  container: HTMLDivElement;
  /** Inside `container`, where the pages go. */
  viewer: HTMLDivElement;
  newLink: () => Promise<SignedLink>;
  /** A piece that did not come: the page stays blank until the screen opens it again. */
  onFailure: (error: unknown) => void;
  onPage: (page: number) => void;
  /** Where to open it: the page it was on when the screen had to open it again. */
  startPage?: number;
  signal: AbortSignal;
}

/** Opens a catalog in `container`. Rejects with a `UserFacingError` the screen can show. */
export async function openCatalog(options: OpenOptions): Promise<OpenedCatalog> {
  const { container, viewer: viewerElement, newLink, onFailure, onPage, signal } = options;
  const startPage = options.startPage ?? 1;
  const { pdfjs, viewer } = await loadPdfJs().catch((error: unknown) => {
    throw isMissingFile(error) ? new NewVersionError() : new UserFacingError(OPEN_FAILED);
  });
  signal.throwIfAborted();
  const reader = new BucketReader(newLink, signal);
  const { size } = await reader.link();
  // Closed while the link was on its way: nothing of pdf.js is started.
  signal.throwIfAborted();

  class BucketRange extends pdfjs.PDFDataRangeTransport {
    override requestDataRange(begin: number, end: number): void {
      reader.read(begin, end).then(
        (chunk) => {
          if (!signal.aborted) this.onDataRange(begin, chunk);
        },
        (error: unknown) => {
          if (!signal.aborted) onFailure(error);
        },
      );
    }
  }

  const task = pdfjs.getDocument({
    range: new BucketRange(size, null),
    rangeChunkSize: CHUNK_BYTES,
    // Only what the page on screen needs: never the rest in the background, never the whole
    // file as a stream.
    disableAutoFetch: true,
    disableStream: true,
    cMapUrl: `${ASSETS}cmaps/`,
    standardFontDataUrl: `${ASSETS}standard_fonts/`,
    wasmUrl: `${ASSETS}wasm/`,
    iccUrl: `${ASSETS}iccs/`,
    enableXfa: false,
  });
  signal.addEventListener('abort', () => void task.destroy(), { once: true });

  let document: Awaited<typeof task.promise>;
  try {
    document = await task.promise;
  } catch (error) {
    if (signal.aborted) throw signal.reason;
    if (error instanceof UserFacingError) throw error;
    if (isMissingFile(error)) throw new NewVersionError();
    throw new UserFacingError(OPEN_FAILED);
  }
  if (signal.aborted) {
    void task.destroy();
    throw signal.reason;
  }

  const eventBus = new viewer.EventBus();
  const linkService = new viewer.PDFLinkService({
    eventBus,
    externalLinkTarget: viewer.LinkTarget.BLANK,
    externalLinkRel: 'noopener noreferrer nofollow',
  });
  const pdfViewer = new viewer.PDFViewer({
    container,
    viewer: viewerElement,
    eventBus,
    linkService,
    annotationMode: pdfjs.AnnotationMode.ENABLE,
    // A catalog is read, never written on: without the editor pdf.js loads less and a
    // finger on the page never starts drawing.
    annotationEditorMode: pdfjs.AnnotationEditorType.DISABLE,
    // A phone does not have the memory for a whole page at 6×: past this pdf.js draws it
    // blurrier instead of failing.
    maxCanvasPixels: 2 ** 24,
  });
  linkService.setViewer(pdfViewer);
  eventBus.on('pagesinit', () => {
    pdfViewer.currentScaleValue = 'page-width';
    if (startPage > 1) pdfViewer.currentPageNumber = Math.min(startPage, document.numPages);
  });
  eventBus.on('pagechanging', ({ pageNumber }: { pageNumber: number }) => onPage(pageNumber));
  pdfViewer.setDocument(document);
  linkService.setDocument(document, null);

  // Two fingers zoom on the page, as on any phone, and not the whole application.
  const gestures = new AbortController();
  new pdfjs.TouchManager({
    container,
    isPinchingDisabled: () => false,
    isPinchingStopped: () => false,
    onPinching: (origin: [number, number], previous: number, distance: number) => {
      const factor = distance / previous;
      const next = pdfViewer.currentScale * factor;
      if (next > MAX_SCALE || next < MIN_SCALE) return;
      pdfViewer.updateScale({ drawingDelay: 400, scaleFactor: factor, origin });
    },
    signal: gestures.signal,
  });

  return {
    pages: document.numPages,
    page: () => pdfViewer.currentPageNumber,
    goTo: (page) => {
      pdfViewer.currentPageNumber = Math.min(Math.max(1, Math.round(page)), document.numPages);
    },
    zoomIn: () => {
      if (pdfViewer.currentScale < MAX_SCALE) pdfViewer.increaseScale();
    },
    zoomOut: () => {
      if (pdfViewer.currentScale > MIN_SCALE) pdfViewer.decreaseScale();
    },
    fitWidth: () => {
      pdfViewer.currentScaleValue = 'page-width';
    },
    destroy: () => {
      gestures.abort();
      pdfViewer.setDocument(null as never);
      linkService.setDocument(null, null);
      void task.destroy();
    },
  };
}
